import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import RouteErrorBoundary from '@/components/RouteErrorBoundary';
import Layout from '@/components/Layout';
import KeyboardShortcuts from '@/components/KeyboardShortcuts';
import ShortcutsHelpDialog from '@/components/ShortcutsHelpDialog';
import GlobalSearch from '@/components/GlobalSearch';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import ComingSoon from '@/pages/ComingSoon';
import ActivityLogs from '@/pages/ActivityLogs';
import { Upload } from 'lucide-react';
import ClientsList from '@/pages/ClientsList';
import CompactTravellersList from '@/pages/CompactTravellersList';
import CompactInvoicesList from '@/pages/CompactInvoicesList';
import CompactVendorsList from '@/pages/CompactVendorsList';
import AddClient from '@/pages/AddClient';
import DocumentExpiry from '@/pages/DocumentExpiry';
import Templates from '@/pages/Templates';
import TaxCalendar from '@/pages/TaxCalendar';
import EditTraveller from '@/pages/EditTraveller';
import TravellerDetail from '@/pages/TravellerDetail';
import QuotesList from '@/pages/QuotesList';
import CreateQuote from '@/pages/CreateQuote';
import EditQuote from '@/pages/EditQuote';
import ItinerariesList from '@/pages/ItinerariesList';
import ItineraryBuilder from '@/pages/ItineraryBuilder';
import ItineraryPreview from '@/pages/ItineraryPreview';
import VoucherPreviewPage from '@/pages/VoucherPreviewPage';
import GSTTCSReport from '@/pages/GSTTCSReport';
import HistoricalTaxReport from '@/pages/HistoricalTaxReport';
import GSTReport from '@/pages/GSTReport';
import TCSReport from '@/pages/TCSReport';
import ReconciliationReport from '@/pages/ReconciliationReport';
import ReconstructedAccounts from '@/pages/ReconstructedAccounts';
import ReviewQueue from '@/pages/ReviewQueue';
import FxReport from '@/pages/FxReport';
import TallyExport from '@/pages/TallyExport';
import BankAccounts from '@/pages/BankAccounts';
import Destinations from '@/pages/Destinations';
import VendorAdvances from '@/pages/VendorAdvances';
import VendorReimbursements from '@/pages/VendorReimbursements';
import PricingAudit from '@/pages/PricingAudit';
import OrphanDetector from '@/pages/OrphanDetector';
import AiUsageDashboard from '@/pages/AiUsageDashboard';
import WhatsAppInbox from '@/pages/WhatsAppInbox';
import WhatsAppTemplates from '@/pages/WhatsAppTemplates';
import WhatsAppAutomation from '@/pages/WhatsAppAutomation';
import Leads from '@/pages/Leads';
import Ledger from '@/pages/Ledger';
import Analytics from '@/pages/Analytics';
import Tools from '@/pages/Tools';
import Admin from '@/pages/Admin';
import PublicItinerary from '@/pages/PublicItinerary';
import PublicInvoice from '@/pages/PublicInvoice';
import CreditNotes from '@/pages/CreditNotes';
import CreditNotePreview from '@/pages/CreditNotePreview';
import Accounting from '@/pages/Accounting';
import ExportCenter from '@/pages/ExportCenter';
import Settings from '@/pages/Settings';
import AccountsDashboard from '@/pages/AccountsDashboard';
import HRDashboard from '@/pages/HRDashboard';
import { isPathAllowed, roleHome } from '@/utils/roles';
import UserManagement from '@/pages/UserManagement';
import TeamDashboard from '@/pages/TeamDashboard';
import Vouchers from '@/pages/Vouchers';
import Vendors from '@/pages/Vendors';
import PublicVoucher from '@/pages/PublicVoucher';
import PublicBundle from '@/pages/PublicBundle';
import PaymentSuccess from '@/pages/PaymentSuccess';
import ClientPortal from '@/pages/ClientPortal';
import PaymentLinkLanding from '@/pages/PaymentLinkLanding';
import PaymentLinks from '@/pages/PaymentLinks';
import WebsiteCMS from '@/pages/WebsiteCMS';
import DemoVoucher from '@/pages/DemoVoucher';
import ClientVoucherPreview from '@/pages/client/VoucherPreview';
import ClientLanding from '@/pages/client/ClientLanding';
import ChangePassword from '@/pages/ChangePassword';
import FinanceReceipts from '@/pages/FinanceReceipts';
import { useIsMobile } from '@/hooks/useIsMobile';
import MobileTabShell from '@/mobile/MobileTabShell';
import MobileScreen from '@/mobile/MobileScreen';
import MobileTripDetail from '@/mobile/screens/MobileTripDetail';
import MobileTravellerProfile from '@/mobile/screens/MobileTravellerProfile';
import MobileLeadDetail from '@/mobile/screens/MobileLeadDetail';
import './index.css';

// Step 8 — legacy /preview/:id → canonical /itinerary/:id (preserves query).
function ItineraryRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/itinerary/${id}${location.search || ''}`} replace />;
}

// Redirect Accounts/HR away from routes outside their allow-list (defense in
// depth on top of the backend role guards, which are the real boundary).
function RoleScopedRoutes({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const role = (user?.role || '').toLowerCase();
  if (!isPathAllowed(role, location.pathname)) {
    return <Navigate to={roleHome(role)} replace />;
  }
  return children;
}

// Hostname gating — the public client domain serves ONLY client-facing routes;
// the CRM/agent app runs on every other host (crm.travcations.com + preview).
const CLIENT_HOSTS = ['app.travcations.com'];
const CRM_HOST = 'crm.travcations.com';
const isClientHost = typeof window !== 'undefined' && CLIENT_HOSTS.includes(window.location.hostname);

// Shared public, client-facing routes (no agent chrome). Used by BOTH the
// agent router (so previously-shared crm.travcations.com/{ref} links keep
// working) and the dedicated client router on app.travcations.com.
function publicClientRoutes() {
  return [
    <Route key="portal" path="/portal/:token" element={<ClientPortal />} />,
    <Route key="voucher" path="/voucher/:voucherId" element={<PublicVoucher />} />,
    <Route key="bundle" path="/bundle-preview/:tripId" element={<PublicBundle />} />,
    <Route key="iti-legacy" path="/itinerary-preview/:id" element={<PublicItinerary />} />,
    // Step 8 — clean client URLs. Canonical itinerary route + legacy redirect.
    <Route key="iti" path="/itinerary/:id" element={<PublicItinerary />} />,
    <Route key="iti-redirect" path="/preview/:id" element={<ItineraryRedirect />} />,
    // Bugfix (2026-08-31) — "/itinerary/:id/preview" (extra /preview suffix,
    // easy typo when combining the 2 canonical patterns) matched no route and
    // fell through to the authenticated catch-all, so a bookmarked/mistyped
    // link 401'd to the login wall instead of the public page.
    <Route key="iti-preview-alias" path="/itinerary/:id/preview" element={<ItineraryRedirect />} />,
    <Route key="inv" path="/invoice-preview/:invoiceNumber" element={<PublicInvoice />} />,
    <Route key="cn-preview" path="/credit-note-preview/:creditNoteId" element={<CreditNotePreview />} />,
    <Route key="pay-success" path="/payment/success" element={<PaymentSuccess />} />,
    <Route key="pay" path="/pay/:token" element={<PaymentLinkLanding />} />,
    <Route key="demo-type" path="/demo/voucher/:type" element={<DemoVoucher />} />,
    <Route key="demo" path="/demo/voucher" element={<DemoVoucher />} />,
    // Clean, ref-first public URLs ({host}/{public_ref}/…). public_ref = ITI…
    // (itinerary) / TRP… (payment). No internal ids. The action-first routes
    // above are kept so previously-shared links keep working.
    <Route key="ref-preview" path="/:id/preview" element={<PublicItinerary />} />,
    <Route key="ref-pay" path="/:token/pay" element={<PaymentLinkLanding />} />,
    // Client-facing trip voucher ({ITI_ref}/voucher) — standalone, print-optimised.
    <Route key="voucher-iti" path="/:tripid/voucher/itinerary" element={<ClientVoucherPreview />} />,
    <Route key="voucher-trip" path="/:tripid/voucher" element={<ClientVoucherPreview />} />,
  ];
}

// On the client host, any non-client (agent/CRM) path bounces to the CRM domain
// so crm.travcations.com routes are strictly blocked on app.travcations.com.
function ClientRedirect() {
  React.useEffect(() => {
    const { pathname, search, hash } = window.location;
    window.location.replace(`https://${CRM_HOST}${pathname}${search}${hash}`);
  }, []);
  return null;
}

// Dedicated router for app.travcations.com — client-facing pages only.
function ClientRouter() {
  return (
    <Routes>
      <Route path="/" element={<ClientLanding />} />
      {publicClientRoutes()}
      <Route path="*" element={<ClientRedirect />} />
    </Routes>
  );
}

// The full agent/CRM app (crm.travcations.com + preview + any non-client host).
function AgentRouter() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const canReports = isAdmin || (user?.role || '').toLowerCase() === 'accounts';

  // Check URL fragment for session_id (Google OAuth callback)
  // This must be synchronous - before ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Agent-only public auth routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Shared public client-facing routes (also served on the CRM host) */}
      {publicClientRoutes()}

      {/* Protected App — dedicated MobileApp on phones, desktop Layout otherwise */}
      <Route path="/*" element={
        <ProtectedRoute>
          {isMobile ? (
            <MobileApp user={user} logout={logout} />
          ) : (
          <Layout user={user} logout={logout}>
            <KeyboardShortcuts />
            <ShortcutsHelpDialog />
            <GlobalSearch />
            <RoleScopedRoutes>
            <RouteErrorBoundary resetKey={location.pathname}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/accounts" element={<AccountsDashboard />} />
              <Route path="/hr" element={<HRDashboard />} />
              <Route path="/clients" element={<CompactTravellersList />} />
              <Route path="/destinations" element={<Destinations />} />
              <Route path="/clients/legacy" element={<ClientsList />} />
              <Route path="/clients/new" element={<AddClient />} />
              <Route path="/clients/:ucc" element={<TravellerDetail />} />
              <Route path="/clients/:ucc/edit" element={<EditTraveller />} />
              <Route path="/document-alerts" element={<DocumentExpiry />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/compliance" element={<TaxCalendar />} />
              <Route path="/quotes" element={<QuotesList />} />
              <Route path="/quotes/new" element={<CreateQuote />} />
              <Route path="/quotes/:id/edit" element={<EditQuote />} />
              <Route path="/itineraries" element={<ItinerariesList />} />
              <Route path="/itineraries/new" element={<ItineraryBuilder />} />
              <Route path="/itineraries/:id" element={<ItineraryBuilder />} />
              <Route path="/itineraries/:id/preview" element={<ItineraryPreview />} />
              <Route path="/accounting" element={canReports ? <Accounting /> : <Navigate to="/" replace />} />
              <Route path="/accounting/gst-tcs" element={canReports ? <GSTTCSReport /> : <Navigate to="/" replace />} />
              <Route path="/accounting/gst-tcs/historical" element={canReports ? <HistoricalTaxReport /> : <Navigate to="/" replace />} />
              <Route path="/reports/gst" element={canReports ? <GSTReport /> : <Navigate to="/" replace />} />
              <Route path="/reports/tcs" element={canReports ? <TCSReport /> : <Navigate to="/" replace />} />
              <Route path="/reports/reconciliation" element={canReports ? <ReconciliationReport /> : <Navigate to="/" replace />} />
              <Route path="/reports/reconstructed-accounts" element={canReports ? <ReconstructedAccounts /> : <Navigate to="/" replace />} />
              <Route path="/reports/review-queue" element={canReports ? <ReviewQueue /> : <Navigate to="/" replace />} />
              <Route path="/reports/fx" element={canReports ? <FxReport /> : <Navigate to="/" replace />} />
              <Route path="/reports/tally-export" element={canReports ? <TallyExport /> : <Navigate to="/" replace />} />
              <Route path="/accounting/bank-accounts" element={canReports ? <BankAccounts /> : <Navigate to="/" replace />} />
              <Route path="/accounting/vendor-advances" element={canReports ? <VendorAdvances /> : <Navigate to="/" replace />} />
              <Route path="/finance/reimbursements" element={canReports ? <VendorReimbursements /> : <Navigate to="/" replace />} />
              <Route path="/admin/pricing-audit" element={isAdmin ? <PricingAudit /> : <Navigate to="/" replace />} />
              <Route path="/admin/orphan-detector" element={isAdmin ? <OrphanDetector /> : <Navigate to="/" replace />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/ledger" element={<Ledger />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/ai-usage" element={isAdmin ? <AiUsageDashboard /> : <Navigate to="/" replace />} />
              <Route path="/whatsapp" element={<WhatsAppInbox />} />
              <Route path="/whatsapp/templates" element={isAdmin ? <WhatsAppTemplates /> : <Navigate to="/" replace />} />
              <Route path="/whatsapp/automation" element={isAdmin ? <WhatsAppAutomation /> : <Navigate to="/" replace />} />
              <Route path="/vouchers" element={<Vouchers />} />
              <Route path="/voucher-preview/:voucherId" element={<VoucherPreviewPage />} />
              <Route path="/vendors" element={<CompactVendorsList />} />
              <Route path="/invoices" element={<CompactInvoicesList />} />
              {/* Alias used by the Dashboard "Payments Due" card — renders the
                  same invoice list so /finance/invoices?status=outstanding
                  opens the outstanding-filtered view. */}
              <Route path="/finance/invoices" element={<CompactInvoicesList />} />
              {/* Phase 16.2 — `/invoices/new` and `/leads/new` redirect into
                  the list view with `?new=1` so the modal auto-opens. Keeps
                  bookmarks + QuickActions tiles working without a separate
                  dedicated "new" page. */}
              <Route path="/invoices/new" element={<Navigate to="/invoices?new=1" replace />} />
              <Route path="/leads/new" element={<Navigate to="/leads?new=1" replace />} />
              <Route path="/vendors/legacy" element={<Vendors />} />
              <Route path="/payment-links" element={<PaymentLinks />} />
              <Route path="/finance/receipts" element={<FinanceReceipts />} />
              <Route path="/finance/credit-notes" element={<CreditNotes />} />
              <Route path="/website" element={<WebsiteCMS />} />
              <Route path="/exports" element={<ExportCenter />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/users" element={<UserManagement />} />
              <Route path="/team-dashboard" element={<TeamDashboard />} />
              <Route path="/change-password" element={<ChangePassword />} />
              {/* FIX 5 (Task 5) — mapped-but-unbuilt tools: friendly placeholder
                  instead of a blank page. */}
              <Route path="/import" element={<ComingSoon title="Bulk Import" description="Import leads, travellers and trips from CSV/Excel. This tool is on the way." icon={Upload} />} />
              <Route path="/activity-logs" element={<ActivityLogs />} />
            </Routes>
            </RouteErrorBoundary>
            </RoleScopedRoutes>
          </Layout>
          )}
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// Top-level router — branch by hostname (client domain vs. agent/CRM domain).
function AppRouter() {
  return isClientHost ? <ClientRouter /> : <AgentRouter />;
}

/**
 * MobileApp — the dedicated phone experience (<768px). Native tab shell for
 * the 4 primary destinations + native trip/traveller detail. All other routes
 * (editors, settings, lists) reuse the existing pages wrapped in a mobile
 * back-bar so every link keeps working. Desktop is untouched.
 */
function MobileApp() {
  const location = useLocation();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Routes>
        {/* Native mobile detail screens */}
        <Route path="/m/trips/:id" element={<MobileTripDetail />} />
        <Route path="/m/clients/:ucc" element={<MobileTravellerProfile />} />
        <Route path="/m/leads/:leadId" element={<MobileLeadDetail />} />
        <Route path="/clients/:ucc" element={<MobileTravellerProfile />} />

        {/* Reused desktop pages wrapped for mobile */}
        <Route path="/quotes/new" element={<MobileScreen title="New Enquiry"><CreateQuote /></MobileScreen>} />
        <Route path="/quotes/:id/edit" element={<MobileScreen title="Edit Trip"><EditQuote /></MobileScreen>} />
        <Route path="/itineraries/new" element={<MobileScreen title="New Trip"><ItineraryBuilder /></MobileScreen>} />
        <Route path="/itineraries/:id" element={<MobileScreen title="Itinerary"><ItineraryBuilder /></MobileScreen>} />
        <Route path="/itineraries/:id/preview" element={<ItineraryPreview />} />
        <Route path="/clients/new" element={<MobileScreen title="New Traveller"><AddClient /></MobileScreen>} />
        <Route path="/clients/:ucc/edit" element={<MobileScreen title="Edit Traveller"><EditTraveller /></MobileScreen>} />
        <Route path="/leads" element={<MobileScreen title="Leads"><Leads /></MobileScreen>} />
        <Route path="/settings" element={<MobileScreen title="Settings"><Settings /></MobileScreen>} />
        <Route path="/templates" element={<MobileScreen title="Templates"><Templates /></MobileScreen>} />
        <Route path="/compliance" element={<MobileScreen title="Tax Calendar"><TaxCalendar /></MobileScreen>} />
        <Route path="/users" element={<MobileScreen title="Team"><UserManagement /></MobileScreen>} />
        <Route path="/admin" element={<MobileScreen title="Admin"><Admin /></MobileScreen>} />
        <Route path="/vouchers" element={<MobileScreen title="Vouchers"><Vouchers /></MobileScreen>} />
        <Route path="/voucher-preview/:voucherId" element={<MobileScreen title="Voucher"><VoucherPreviewPage /></MobileScreen>} />
        <Route path="/payment-links" element={<MobileScreen title="Payment Links"><PaymentLinks /></MobileScreen>} />
        <Route path="/finance/invoices" element={<MobileScreen title="Invoices"><CompactInvoicesList /></MobileScreen>} />
        <Route path="/invoices" element={<MobileScreen title="Invoices"><CompactInvoicesList /></MobileScreen>} />
        <Route path="/finance/receipts" element={<MobileScreen title="Receipts"><FinanceReceipts /></MobileScreen>} />
        <Route path="/ledger" element={<MobileScreen title="Ledger"><Ledger /></MobileScreen>} />
        <Route path="/change-password" element={<MobileScreen title="Profile"><ChangePassword /></MobileScreen>} />
        <Route path="/team-dashboard" element={<MobileScreen title="Team"><TeamDashboard /></MobileScreen>} />
        <Route path="/analytics" element={<MobileScreen title="Analytics"><Analytics /></MobileScreen>} />
        <Route path="/accounting" element={(role === 'admin' || role === 'accounts') ? <MobileScreen title="Accounting"><Accounting /></MobileScreen> : <Navigate to="/" replace />} />
        <Route path="/import" element={<MobileScreen title="Bulk Import"><ComingSoon title="Bulk Import" description="Import leads, travellers and trips from CSV/Excel. This tool is on the way." icon={Upload} /></MobileScreen>} />
        <Route path="/activity-logs" element={<MobileScreen title="Activity Logs"><ActivityLogs /></MobileScreen>} />
        <Route path="/accounts" element={<MobileScreen title="Accounts"><AccountsDashboard /></MobileScreen>} />
        <Route path="/hr" element={<MobileScreen title="Team"><HRDashboard /></MobileScreen>} />

        {/* Tab shell — Dashboard / Leads / Trips / Settings */}
        <Route path="*" element={
          role === 'accounts' ? <MobileScreen title="Accounts"><AccountsDashboard /></MobileScreen>
            : role === 'hr' ? <MobileScreen title="Team"><HRDashboard /></MobileScreen>
              : <MobileTabShell />
        } />
      </Routes>
    </RouteErrorBoundary>
  );
}


function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
      <Toaster position="bottom-right" richColors />
    </BrowserRouter>
  );
}

export default App;
